import { DatabaseEntities } from './base';

import type { Maybe, Nullable } from '@nova/core';

export interface YardTaskChecklist {
  complete: boolean;
  description: string;
}

export enum YardTaskType {
  MOVE = 'move',
  CHECK = 'check'
}

export enum YardTaskStatus {
  CREATED = 'created',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum YardTaskEvent {
  CREATED = 'created',
  ASSIGNED = 'assigned',
  UNASSIGNED = 'unassigned',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  FLAGGED = 'flagged',
  ISSUE = 'issue'
}

export const YardTaskCheckEntities = Object.freeze([
  DatabaseEntities.AssetContainer,
  DatabaseEntities.Spot,
  DatabaseEntities.SpotArea
] as const);

export enum YardTaskCheckEntity {
  AssetContainer = DatabaseEntities.AssetContainer,
  Spot = DatabaseEntities.Spot,
  SpotArea = DatabaseEntities.SpotArea
}

/**
 * Categories for Yard Task Check
 */
export enum YardTaskCheckCategory {
  IDENTIFICATION_CHECK = 'identification-check',
  INSPECTION = 'inspection',
  LOCATION_CHECK = 'location-check',
  STATUS_CHECK = 'status-check',
  CONDITION_ASSESSMENT = 'condition-assessment',
  INVENTORY_CHECK = 'inventory-check',
  MOVEMENT_CHECK = 'movement-check'
}

/**
 * Outcome of a yard check task for a specific asset container at a location
 * (e.g. driver confirmed present vs reported missing).
 */
export enum YardTaskCheckLocationOutcome {
  CONFIRMED = 'confirmed',
  MISSING = 'missing'
}

/**
 * Yard Task Event data structure
 */
export type YardTaskEventData = {
  id: string;
  createDateTime: string;
  event: string;
  reason: Nullable<string>;
  data: {
    user: { firstName?: Maybe<string>; lastName?: Maybe<string> };
  };
  yardTaskId: string;
};

/**
 * Categories for Asset Container tasks
 */
export const YARD_TASK_ASSET_CONTAINER_CATEGORIES = [
  YardTaskCheckCategory.IDENTIFICATION_CHECK,
  YardTaskCheckCategory.INSPECTION,
  YardTaskCheckCategory.LOCATION_CHECK
] as const;

export type YardTaskAssetContainerCategory = (typeof YARD_TASK_ASSET_CONTAINER_CATEGORIES)[number];

/**
 * Categories for Spot tasks
 */
export const YARD_TASK_SPOT_CATEGORIES = [
  YardTaskCheckCategory.INSPECTION,
  YardTaskCheckCategory.STATUS_CHECK
] as const;

export type YardTaskSpotCategory = (typeof YARD_TASK_SPOT_CATEGORIES)[number];

/**
 * Categories for Spot Area tasks
 */
export const YARD_TASK_SPOT_AREA_CATEGORIES = [
  YardTaskCheckCategory.CONDITION_ASSESSMENT,
  YardTaskCheckCategory.INVENTORY_CHECK,
  YardTaskCheckCategory.MOVEMENT_CHECK
] as const;

export type YardTaskSpotAreaCategory = (typeof YARD_TASK_SPOT_AREA_CATEGORIES)[number];

/**
 * Type guard functions to check category validity for each entity type
 * @param category - The category to validate
 * @returns True if the category is valid for the given entity type, false otherwise
 */
export function isValidAssetContainerCategory(
  category: string
): category is YardTaskAssetContainerCategory {
  return YARD_TASK_ASSET_CONTAINER_CATEGORIES.includes(category as YardTaskAssetContainerCategory);
}

/**
 * Type guard function to check if a category is valid for Spot tasks
 * @param category - The category to validate
 * @returns True if the category is valid for Spot tasks, false otherwise
 */
export function isValidSpotCategory(category: string): category is YardTaskSpotCategory {
  return YARD_TASK_SPOT_CATEGORIES.includes(category as YardTaskSpotCategory);
}

/**
 * Type guard function to check if a category is valid for Spot Area tasks
 * @param category - The category to validate
 * @returns True if the category is valid for Spot Area tasks, false otherwise
 */
export function isValidSpotAreaCategory(category: string): category is YardTaskSpotAreaCategory {
  return YARD_TASK_SPOT_AREA_CATEGORIES.includes(category as YardTaskSpotAreaCategory);
}

/**
 * Helper function to get available categories for a given entity type
 * @param entityType - The type of entity to get categories for
 * @returns An array of available categories for the given entity type
 */
export function getAvailableCategoriesForEntity(
  entityType: YardTaskCheckEntity
): YardTaskCheckCategory[] {
  switch (entityType) {
    case YardTaskCheckEntity.AssetContainer:
      return [...YARD_TASK_ASSET_CONTAINER_CATEGORIES];
    case YardTaskCheckEntity.Spot:
      return [...YARD_TASK_SPOT_CATEGORIES];
    case YardTaskCheckEntity.SpotArea:
      return [...YARD_TASK_SPOT_AREA_CATEGORIES];
    default:
      return [];
  }
}

/**
 * Helper function to validate if a category is valid for a specific entity type
 * @param category - The category to validate
 * @param entityType - The type of entity to validate the category for
 * @returns True if the category is valid for the given entity type, false otherwise
 */
export function isValidCategoryForEntity(
  category: string,
  entityType: YardTaskCheckEntity
): boolean {
  switch (entityType) {
    case YardTaskCheckEntity.AssetContainer:
      return isValidAssetContainerCategory(category);
    case YardTaskCheckEntity.Spot:
      return isValidSpotCategory(category);
    case YardTaskCheckEntity.SpotArea:
      return isValidSpotAreaCategory(category);
    default:
      return false;
  }
}

export function getCategoryLabelTranslationKey(category: YardTaskCheckCategory): Nullable<string> {
  switch (category) {
    case YardTaskCheckCategory.IDENTIFICATION_CHECK:
      return 'yard.drawers.createTaskDrawer.labels.identificationCheck';
    case YardTaskCheckCategory.INSPECTION:
      return 'yard.drawers.createTaskDrawer.labels.inspection';
    case YardTaskCheckCategory.LOCATION_CHECK:
      return 'yard.drawers.createTaskDrawer.labels.locationCheck';
    case YardTaskCheckCategory.STATUS_CHECK:
      return 'yard.drawers.createTaskDrawer.labels.statusCheck';
    case YardTaskCheckCategory.CONDITION_ASSESSMENT:
      return 'yard.drawers.createTaskDrawer.labels.conditionAssessment';
    case YardTaskCheckCategory.INVENTORY_CHECK:
      return 'yard.drawers.createTaskDrawer.labels.inventoryCheck';
    case YardTaskCheckCategory.MOVEMENT_CHECK:
      return 'yard.drawers.createTaskDrawer.labels.movementCheck';
    default:
      return null;
  }
}

export enum YardTaskFlags {
  FLAGGED = 'flagged',
  URGENT = 'urgent',
  ISSUE = 'issue'
}

export type YardTaskUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type TimeInput = {
  time?: string;
  amPm?: string;
};

export type ChecklistItem = YardTaskChecklist;

/**
 * Base properties shared across all yard task types (for forms)
 */
export type BaseTaskCreateData = {
  description?: Maybe<string>;
  assignee?: Maybe<string>;
  dueByDate?: string;
  dueByTime?: TimeInput;
  tags?: string[];
};

/**
 * Base properties for view data (with full user objects and date fields)
 */
export type BaseTaskViewData = {
  description?: Maybe<string>;
  tags?: string[];

  assignedTo?: Maybe<YardTaskUser>;
  assignedBy?: Maybe<YardTaskUser>;
  assignedDateTime?: Maybe<string>;
  dueDateTime?: Maybe<string>; // ISO string from API
  createdBy?: Maybe<YardTaskUser>;
  createdAt?: Maybe<string>;
  lastChangedBy?: Maybe<YardTaskUser>;
  lastChangedDateTime?: Maybe<string>;
};

/**
 * Check Task Form Data - format expected by create/edit forms
 */
export type CheckTaskFormData = BaseTaskCreateData & {
  type: 'check';
  checklist: ChecklistItem[];
  subject: string;
  spot?: Maybe<string>;
  area?: Maybe<string>;
  asset?: Maybe<string>;
  category?: string;
  // Edit-specific fields (optional for create flow)
  id?: Maybe<string>;
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  createdAt?: string;
  createdBy?: YardTaskUser;
  status?: string;
};

/**
 * Check Task View Data - format for presentation/viewing (with full objects)
 */
export type CheckTaskViewData = BaseTaskViewData & {
  type: 'check';
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  id?: Maybe<string>;
  category: YardTaskCheckCategory;
  subject: string;
  checklist: ChecklistItem[];
  relatedEntity?: YardTaskCheckEntity;
  relatedId?: Maybe<string>;
  relatedObject?: Maybe<{
    id: string;
    code?: string;
    type?: string;
    name?: string;
    [key: string]: any;
  }>;
  status: string;
  events?: YardTaskEventData[];
};

export type TaskApiRequestData = {
  warehouseId: string;
  description?: Maybe<string>;
  assignedTo?: Maybe<string>;
  dueDateTime?: Maybe<string>;
  tags?: string[];
  status?: string;
};

export type CheckTaskApiRequestData = TaskApiRequestData & {
  type: 'check';
  child: Maybe<{
    checklist: ChecklistItem[];
    category?: Maybe<YardTaskCheckCategory>;
    relatedEntity: YardTaskCheckEntity;
    relatedId: Nullable<string>;
  }>;
};

export type CheckTaskApiResponseData = {
  id?: string;
  orgId?: string;
  warehouseId?: string;
  order?: number;
  refNum?: number;

  assignedBy?: YardTaskUser;
  assignedTo?: Maybe<YardTaskUser>;
  assignedDateTime?: Maybe<string>;
  dueDateTime?: Maybe<string>;
  status?: string;
  childTaskId?: string;
  type: 'check';
  tags?: string[];
  warehouse?: {
    id: string;
    name: string;
    facilityNumber?: Maybe<string>;
  };
  child?: {
    category?: YardTaskCheckCategory;
    relatedEntity?: YardTaskCheckEntity;
    relatedId?: string;
    relatedObject?: {
      id: string;
      code?: string;
      type?: string;
      name?: string;
      spotAreaId?: string;
      observation?: Maybe<string>;
      [key: string]: any;
    };
    checklist?: ChecklistItem[];
  };
  createDateTime?: string;
  createdBy?: YardTaskUser;
  lastChangedBy?: YardTaskUser;
  lastChangedDateTime?: string;
  events?: YardTaskEventData[];
};

/**
 * Move Task Form Data - format expected by create/edit forms
 */
export type MoveTaskFormData = BaseTaskCreateData & {
  type: 'move';
  asset?: Maybe<string>;
  originSpot?: Maybe<string>;
  destinationSpot?: Maybe<string>;
  // Edit-specific fields (optional for create flow)
  id?: Maybe<string>;
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  createdAt?: string;
  createdBy?: YardTaskUser;
  status?: string;
};

/**
 * Move Task View Data - format for presentation/viewing (with full objects)
 */
export type MoveTaskViewData = BaseTaskViewData & {
  type: 'move';
  id?: Maybe<string>;
  originSpot?: Maybe<{
    id: string;
    code: string;
    type: string;
    observation: Nullable<string>;
    spotAreaId?: string;
  }>;
  destinationSpot?: Maybe<{
    id: string;
    code: string;
    type: string;
    observation: Nullable<string>;
    spotAreaId?: string;
  }>;
  assetContainer?: Maybe<{
    id: string;
    type: string;
    state: Nullable<string>;
    code: string;
    notes: Nullable<string>;
  }>;
  originSpotArea?: Maybe<{
    id: string;
    name: string;
  }>;
  destinationSpotArea?: Maybe<{
    id: string;
    name: string;
  }>;
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  status: string;
  events?: YardTaskEventData[];
};

export type TaskViewData = CheckTaskViewData | MoveTaskViewData;

export type MoveTaskApiRequestData = TaskApiRequestData & {
  type: 'move';
  child: Maybe<{
    assetContainerId?: Maybe<string>;
    originSpotId?: Maybe<string>;
    destinationSpotId?: Maybe<string>;
  }>;
};

export type MoveTaskApiResponseData = {
  id?: string;
  orgId?: string;
  warehouseId?: string;
  order?: number;
  refNum?: number;

  assignedBy?: YardTaskUser;
  assignedTo?: Maybe<YardTaskUser>;
  assignedDateTime?: Maybe<string>;
  dueDateTime?: Maybe<string>;
  status?: string;
  childTaskId?: string;
  type: 'move';
  tags?: string[];
  warehouse?: {
    id: string;
    name: string;
    facilityNumber?: Maybe<string>;
  };
  child?: {
    assetContainer?: {
      id: string;
      type: string;
      state: Nullable<string>;
      code: string;
      notes: Nullable<string>;
    };
    originSpot?: {
      id: string;
      code: string;
      type: string;
      observation: Nullable<string>;
      spotAreaId?: string;
    };
    destinationSpot?: {
      id: string;
      code: string;
      type: string;
      observation: Nullable<string>;
      spotAreaId?: string;
    };
    originSpotArea?: {
      id: string;
      name: string;
    };
    destinationSpotArea?: {
      id: string;
      name: string;
    };
  };
  createDateTime?: string;
  createdBy?: YardTaskUser;
  lastChangedBy?: YardTaskUser;
  lastChangedDateTime?: string;
  events?: YardTaskEventData[];
};

export type TaskApiResponseData = CheckTaskApiResponseData | MoveTaskApiResponseData;
